import { useState, useEffect } from 'react';

const COOKIE_KEY = 'marche_casa_cookie_consent';

type ConsentState = {
  analytics: boolean;
  marketing: boolean;
};

const CookieConsent = () => {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [prefs, setPrefs] = useState<ConsentState>({ analytics: false, marketing: false });

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      setBannerVisible(true);
    }

    const handlePolicy = () => setPolicyOpen(true);
    const handleConsent = () => setConsentOpen(true);
    window.addEventListener('openCookiePolicy', handlePolicy);
    window.addEventListener('openCookieConsent', handleConsent);
    return () => {
      window.removeEventListener('openCookiePolicy', handlePolicy);
      window.removeEventListener('openCookieConsent', handleConsent);
    };
  }, []);

  const saveConsent = (accepted: ConsentState) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...accepted, necessary: true, date: new Date().toISOString() }));
    setBannerVisible(false);
    setConsentOpen(false);
  };

  const acceptAll = () => saveConsent({ analytics: true, marketing: true });
  const rejectAll = () => saveConsent({ analytics: false, marketing: false });
  const saveCustom = () => saveConsent(prefs);

  return (
    <>
      {/* Cookie Banner */}
      {bannerVisible && !consentOpen && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-anthracite-800 border-t border-white/10 px-6 py-5">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex-1">
              <p className="font-sans text-sm text-white/80 leading-relaxed">
                Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo consenso, cookie analitici e di marketing.{' '}
                <button
                  onClick={() => setPolicyOpen(true)}
                  className="underline text-terracotta-200 hover:text-terracotta-100 transition-colors cursor-pointer bg-transparent border-0 p-0 font-sans text-sm"
                >
                  Cookie Policy
                </button>
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
              <button
                onClick={() => { setBannerVisible(false); setConsentOpen(true); }}
                className="font-sans text-xs tracking-widest uppercase px-4 py-2 border border-white/30 text-white/60 hover:border-white/60 hover:text-white transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Personalizza
              </button>
              <button
                onClick={rejectAll}
                className="font-sans text-xs tracking-widest uppercase px-4 py-2 border border-white/30 text-white/60 hover:border-white/60 hover:text-white transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Rifiuta tutto
              </button>
              <button
                onClick={acceptAll}
                className="font-sans text-xs tracking-widest uppercase px-5 py-2 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Accetta tutto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Modal */}
      {consentOpen && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConsentOpen(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-ivory-200 flex items-center justify-between">
              <h2 className="font-serif text-xl text-anthracite-800 font-light">Preferenze Cookie</h2>
              <button
                onClick={() => setConsentOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-anthracite-400 hover:text-anthracite-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-6 flex flex-col gap-5">
              {/* Necessary */}
              <div className="flex items-start justify-between gap-4 p-4 bg-ivory-50 rounded-xl border border-ivory-200">
                <div className="flex-1">
                  <p className="font-sans text-sm font-semibold text-anthracite-700 mb-1">Cookie necessari</p>
                  <p className="font-sans text-xs text-anthracite-400 leading-relaxed">
                    Essenziali per il funzionamento del sito. Non possono essere disattivati.
                  </p>
                </div>
                <div className="flex-shrink-0 mt-0.5">
                  <span className="font-sans text-xs text-terracotta-300 font-semibold uppercase tracking-wider">Sempre attivi</span>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 p-4 bg-ivory-50 rounded-xl border border-ivory-200">
                <div className="flex-1">
                  <p className="font-sans text-sm font-semibold text-anthracite-700 mb-1">Cookie analitici</p>
                  <p className="font-sans text-xs text-anthracite-400 leading-relaxed">
                    Ci aiutano a capire come i visitatori interagiscono con il sito, raccogliendo informazioni in forma anonima.
                  </p>
                </div>
                <button
                  onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                  className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer relative ${prefs.analytics ? 'bg-terracotta-300' : 'bg-anthracite-200'}`}
                  aria-checked={prefs.analytics}
                  role="switch"
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${prefs.analytics ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-4 p-4 bg-ivory-50 rounded-xl border border-ivory-200">
                <div className="flex-1">
                  <p className="font-sans text-sm font-semibold text-anthracite-700 mb-1">Cookie di marketing</p>
                  <p className="font-sans text-xs text-anthracite-400 leading-relaxed">
                    Utilizzati per mostrare annunci pertinenti e misurare l&apos;efficacia delle campagne pubblicitarie.
                  </p>
                </div>
                <button
                  onClick={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                  className={`flex-shrink-0 mt-0.5 w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer relative ${prefs.marketing ? 'bg-terracotta-300' : 'bg-anthracite-200'}`}
                  aria-checked={prefs.marketing}
                  role="switch"
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${prefs.marketing ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            <div className="p-6 border-t border-ivory-200 flex flex-col sm:flex-row gap-3">
              <button
                onClick={rejectAll}
                className="flex-1 font-sans text-xs tracking-widest uppercase px-4 py-3 border border-anthracite-200 text-anthracite-500 hover:border-anthracite-400 transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Rifiuta tutto
              </button>
              <button
                onClick={saveCustom}
                className="flex-1 font-sans text-xs tracking-widest uppercase px-4 py-3 border border-terracotta-300 text-terracotta-300 hover:bg-terracotta-50 transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Salva preferenze
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 font-sans text-xs tracking-widest uppercase px-4 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Accetta tutto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Policy Modal */}
      {policyOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPolicyOpen(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-ivory-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <h2 className="font-serif text-xl text-anthracite-800 font-light">Cookie Policy</h2>
              <button
                onClick={() => setPolicyOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-anthracite-400 hover:text-anthracite-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-6 flex flex-col gap-6 font-sans text-sm text-anthracite-500 leading-relaxed">
              <p className="text-xs text-anthracite-400">Ultimo aggiornamento: aprile 2026</p>

              <section>
                <h3 className="font-semibold text-anthracite-700 mb-2">Cosa sono i cookie</h3>
                <p>I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell&apos;utente durante la navigazione. Vengono utilizzati per far funzionare il sito in modo efficiente e per fornire informazioni ai gestori.</p>
              </section>

              <section>
                <h3 className="font-semibold text-anthracite-700 mb-2">Cookie tecnici necessari</h3>
                <p>Questi cookie sono indispensabili per il corretto funzionamento del sito e non possono essere disattivati. Includono cookie di sessione, preferenze di lingua e consenso cookie. Non raccolgono dati personali identificabili.</p>
              </section>

              <section>
                <h3 className="font-semibold text-anthracite-700 mb-2">Cookie analitici</h3>
                <p>Utilizziamo cookie analitici per raccogliere informazioni aggregate sull&apos;utilizzo del sito (pagine visitate, tempo di permanenza, provenienza del traffico). I dati sono anonimi e ci aiutano a migliorare l&apos;esperienza di navigazione.</p>
              </section>

              <section>
                <h3 className="font-semibold text-anthracite-700 mb-2">Cookie di marketing</h3>
                <p>I cookie di marketing vengono utilizzati per tracciare i visitatori tra i siti web e mostrare annunci pertinenti. Vengono attivati solo previo consenso esplicito dell&apos;utente.</p>
              </section>

              <section>
                <h3 className="font-semibold text-anthracite-700 mb-2">Come gestire i cookie</h3>
                <p>Puoi modificare le tue preferenze in qualsiasi momento cliccando su &quot;Gestisci Cookie&quot; nel footer del sito. Puoi anche configurare il tuo browser per bloccare o eliminare i cookie, ma alcune funzionalità del sito potrebbero non essere disponibili.</p>
              </section>

              <section>
                <h3 className="font-semibold text-anthracite-700 mb-2">Titolare del trattamento</h3>
                <p>Discovering Marche — Ines Felicity Di Nallo — P.IVA 03036380420 — Castelfidardo (AN), Italia</p>
              </section>
            </div>
            <div className="p-6 border-t border-ivory-200 flex justify-end gap-3">
              <button
                onClick={() => { setPolicyOpen(false); setConsentOpen(true); }}
                className="font-sans text-xs tracking-widest uppercase px-5 py-3 border border-terracotta-300 text-terracotta-300 hover:bg-terracotta-50 transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Gestisci preferenze
              </button>
              <button
                onClick={() => setPolicyOpen(false)}
                className="font-sans text-xs tracking-widest uppercase px-5 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all rounded-full cursor-pointer whitespace-nowrap"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
